import {ExpandMore} from "@mui/icons-material";
import {Box, MenuItem, Button, Accordion, AccordionDetails, AccordionSummary, ListItemText, Divider, TextField}
    from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, {ChangeEventHandler, useCallback, useContext, useMemo, useState} from "react";
import { connect } from "react-redux";
import { selectProject } from "../../../redux/selectors/editor";
import { selectConstants } from "../../../redux/selectors/firebase";
import { getConstant, getTagDisplayValue } from "../../../utility/constantsTagsUtils";
import { getInitialStyle, StyleOptions } from "../../../utility/Style";
import { FirebaseContext } from "../../firebase/FirebaseProvider";
import { Constants } from "../../graph/StateTags";
import { Form } from "../../input/Form";
import { TextFieldAuto } from "../../input/TextFieldAuto";
import { FormattingStyleOptions } from "../../sheets/conditionalFormatting/StyleOptions";
import {IConstant} from "./Constant";
import {DatePicker, LocalizationProvider, TimePicker} from "@mui/lab";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import enLocale from "date-fns/locale/en-GB";
import {ChipDetails} from "./ChipDetails";
import {StyleDetails} from "./StyleDetails";

const useStyles = makeStyles((theme) => ({
    chipSwitch: {
        flexBasis: "100%",
        flexGrow: 1
    },
    dateTime: {
        display: "flex",
        marginTop: theme.spacing(1)
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        marginBottom: theme.spacing(1)
    },
    input: {
        flexBasis: "48%",
        flexGrow: 1,
    }
}));
export const intrinsics: {[key:string]: IIntrinsic} = {
    blockName: {
        name: "Block Name",
        type: "string"
    },
    ulid: {
        name: "ULID",
        type: "string"
    }
}

interface IIntrinsic {
    name: string
    type: string
}
interface Props
{
    project: string
    constants: Constants
}

function ConstantStyle({ project, constants }: Props)
{
    const classes = useStyles();
    const { set } = useContext(FirebaseContext);
    const [expanded, setExpanded] = useState("");
    const [constant, setConstant] = useState("");
    const [display, setDisplay] = useState("style");
    const selectedConstant = useMemo((): IConstant | IIntrinsic | null => {
        if (constant in intrinsics) {
            return intrinsics[constant];
        } else if (constant in constants) {
            return constants[constant];
        }
        return null;
    }, [constants, constant]);
    const [value, setValue] = useState<any>("");
    const [styleOptions, setStyleOptions] = useState<StyleOptions>(getInitialStyle());

    const disabledAdd = ! constants[constant] && ! intrinsics[constant];

    const setStyle = useCallback((constant: string, value: string, styleOptions: StyleOptions) =>
    {
        return set(`constants/${project}/${constant}/style/${value}`, styleOptions);
    }, [set, project])

    const setChipStyle = useCallback((constant: string, styleOptions: StyleOptions | "auto") =>
    {
        return set(`constants/${project}/${constant}/chip/`, styleOptions);
    }, [set, project])

    const handleAddStyle = useCallback(() =>
    {
        if (!disabledAdd)
        {
            if (display === "chip")
            {
                setChipStyle(constant, styleOptions);
            } else if (display === "autoChip") {
                setChipStyle(constant, "auto");
            }
            else
            {
                setStyle(constant, value, styleOptions);
            }
            setConstant("");
            setValue("");
            setStyleOptions(getInitialStyle());
        }
    }, [constant, value, styleOptions, disabledAdd, display, setChipStyle, setStyle]);

    const getConstraintValues = (): {[key: string]: string} =>
    {
        if (constants[constant]) {
            const data = Object.values(constants[constant].constraints ?? {}).find(constraint =>
                constraint.type === "select")?.data as { values: { [key: string]: string } };
            const style = constants[constant].style || {};

            return Object.fromEntries(Object.entries(data.values).filter(([id]) => ! style[id]));
        }
        return {};
    }
    const handleChangeValue: ChangeEventHandler<HTMLInputElement> = ({currentTarget}) => {
        setValue(currentTarget.value);
    }
    const handleChangeDate = (value: any) => {
        if (value as Date) {
            setValue(value.getTime());
        } else {
            setValue(Date.parse(value as string));
        }
    }
    const handleChangeConstant: ChangeEventHandler<HTMLInputElement> = ({target}) =>
    {
        const value = target.value as string;
        const constant = constants[value];

        if (constant) {
            setConstant(value);
            if (constant.type === "boolean") {
                setValue("true");
            } else if (constant.type === "date") {
                setValue(Date.now());
            } else {
                setValue("");
            }
        } else if (intrinsics[value]) {
            setConstant(value);
            setValue("");
        }
    }
    const displayInput = display === "style" && (constants[constant] || intrinsics[constant]);
    const showInputSelect = displayInput && Object.values(constants[constant]?.constraints || {})
        .some((constraint) => constraint.type === "select");
    const showInput = !showInputSelect && (selectedConstant?.type === "string" || selectedConstant?.type === "number");

    return <Box m={1}>
        <Form className={classes.form} onCommit={handleAddStyle}>
            <Box display="flex" flexGrow={1}>
                <Box flexGrow={1} marginLeft={1} marginRight={1}>
                    <TextFieldAuto label="Display" value={display} select size="small" fullWidth onChange={(e) => {
                        setDisplay(e.target.value as string);
                        setValue("");
                    }}>
                        <MenuItem value="autoChip">Auto Styled Chip</MenuItem>
                        <MenuItem value="chip">Chip</MenuItem>
                        <MenuItem value="style">Node Style</MenuItem>
                    </TextFieldAuto>
                </Box>
                <Box flexGrow={1} marginLeft={1} marginRight={1}>
                    <TextFieldAuto label="Constant" select size="small" fullWidth onChange={handleChangeConstant}
                                   value={constant}>
                        <MenuItem key="unset" value=""><em>Select Constant</em></MenuItem>
                        {Object.entries(constants).filter(([id, constant]) => constant.name)
                            .map(([id, constant]) =>
                            <MenuItem key={id} value={id}>{constant.name}</MenuItem>
                        )}
                        <Divider/>
                        {Object.entries(intrinsics).map(([id, intrinsic]) =>
                            <MenuItem key={id} value={id}>{intrinsic.name}</MenuItem>
                        )}
                    </TextFieldAuto>
                </Box>
            </Box>
            <Box className={classes.input} marginLeft={1} marginRight={1}>
                {displayInput && showInputSelect &&
                    <TextFieldAuto
                        label={getConstant(constant, constants)?.name ?? constant} select  size="small" margin="dense"
                        fullWidth SelectProps={{
                            fullWidth: true,
                            value: value,
                            onChange: (e) => {
                                setValue(e.target.value);
                            }
                        }}>
                        {Object.entries(getConstraintValues()).map(([id, value]) =>
                            <MenuItem key={id} value={id}>{value}</MenuItem>
                        )}
                    </TextFieldAuto>
                }
                {displayInput && showInput &&
                    <TextFieldAuto type={selectedConstant.type} value={value}
                                   label={getConstant(constant, constants)?.name ?? constant} size="small"
                                   margin="dense" fullWidth onChange={handleChangeValue}/>
                }
                {displayInput && constants[constant].type === "date" &&
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={enLocale}>
                        <Box className={classes.dateTime}>
                            <DatePicker label="Date" onChange={handleChangeDate} value={value}
                                        renderInput={(params) =>
                                            <TextField size="small" sx={{marginRight: 1}} {...params}/>
                            }/>
                            <TimePicker label="Time" onChange={handleChangeDate} value={value}
                                        renderInput={(params) =>
                                            <TextField size="small" {...params}/>
                            }/>
                        </Box>
                    </LocalizationProvider>
                }
            </Box>
            {display !== "autoChip" &&
                <Box className={classes.input}>
                    <FormattingStyleOptions formatStyle={styleOptions} setFormatStyle={setStyleOptions} />
                </Box>
            }
            <Box display="flex" justifyContent="right" marginLeft={1} marginRight={1}>
                <Button disabled={disabledAdd}
                    onClick={handleAddStyle} variant="contained" type="button">
                    Add
                </Button>
            </Box>
        </Form>
        <Divider />
        {
            Object.entries(constants)
                .filter(([id, constant]) => constant.style !== undefined)
                .map(([id, constant]) =>
                    Object.entries(constant?.style ?? {}).map(([value, style]) =>
                        <Accordion key={`${id}_${value}`} expanded={expanded === `${id}_${value}`}
                            onChange={() => setExpanded(expanded === `${id}_${value}` ? "" : `${id}_${value}`)}>
                            <AccordionSummary sx={{ margin: 0 }} expandIcon={<ExpandMore />}>
                                <ListItemText>
                                    {constant.name || intrinsics[id].name}: 
                                    {getTagDisplayValue({ type: id, value }, constants)}
                                </ListItemText>
                            </AccordionSummary>
                            <AccordionDetails sx={{padding: 0}}>
                                <StyleDetails constant={constant} constants={constants} id={id} project={project}
                                              style={style} value={value}/>
                            </AccordionDetails>
                        </Accordion>
                    ))
        }
        {
            Object.entries(constants)
                .filter(([id, constant]) => constant.chip !== undefined)
                .map(([id, constant]) =>
                    <Accordion key={`chips_${id}`} expanded={expanded === `chips_${id}`}
                        onChange={() => setExpanded(expanded === `chips_${id}` ? "" : `chips_${id}`)}>
                        <AccordionSummary sx={{ margin: 0 }} expandIcon={<ExpandMore />}>
                            <ListItemText>Chip: {constant.name}</ListItemText>
                        </AccordionSummary>
                        <AccordionDetails sx={{padding: 0}}>
                            <ChipDetails constant={constant} id={id} project={project} value={value}/>
                        </AccordionDetails>
                    </Accordion>
                )
        }

    </Box >
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) ?? {},
    project: selectProject(store)
});
export default connect(mapStateToProps, {
})(ConstantStyle);